import { useEffect, useState } from 'react'
import { Workflow, ExternalLink, Plus, Trash2, Pause, Play } from 'lucide-react'
import { api } from '@/lib/api'

interface ExternalRef {
  id: string
  name: string
  description: string | null
  n8nWorkflowId: string
  version: number
  status: 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED'
  triggerEvent: string | null
  webhookUrlIn: string | null
  webhookUrlOut: string | null
  editorUrl: string | null
  updatedAt: string
  _count: { enrollments: number; executions: number; goals: number }
}

const N8N_BASE = (import.meta.env.VITE_N8N_URL as string | undefined) ?? 'http://localhost:5678'

export function WorkflowsPage() {
  const [items, setItems] = useState<ExternalRef[]>([])
  const [draftName, setDraftName] = useState('')
  const [draftN8nId, setDraftN8nId] = useState('')
  const [draftTrigger, setDraftTrigger] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function load() {
    const r = await api<{ items: ExternalRef[] }>('/v1/marketing/workflows/external-refs')
    setItems(r.items)
  }

  useEffect(() => {
    void load()
  }, [])

  async function create(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      await api('/v1/marketing/workflows/external-refs', {
        method: 'POST',
        body: JSON.stringify({
          name: draftName,
          n8nWorkflowId: draftN8nId,
          triggerEvent: draftTrigger || undefined,
          editorUrl: `${N8N_BASE}/workflow/${draftN8nId}`,
        }),
      })
      setDraftName('')
      setDraftN8nId('')
      setDraftTrigger('')
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function toggleStatus(r: ExternalRef) {
    const next: ExternalRef['status'] =
      r.status === 'ACTIVE' ? 'PAUSED' : r.status === 'PAUSED' ? 'ACTIVE' : 'ACTIVE'
    await api(`/v1/marketing/workflows/external-refs/${r.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next }),
    })
    await load()
  }

  async function remove(r: ExternalRef) {
    if (!confirm(`¿Eliminar el workflow "${r.name}"?`)) return
    await api(`/v1/marketing/workflows/external-refs/${r.id}`, { method: 'DELETE' })
    await load()
  }

  return (
    <div className="p-6 max-w-6xl">
      <header className="mb-4">
        <h1 className="text-lg font-medium tracking-tight flex items-center gap-2">
          <Workflow className="size-5 text-blueprint-700" />
          Workflows · n8n
        </h1>
        <p className="text-xs font-mono text-ink-700/60">
          motor: <span className="font-medium">n8n self-hosted</span> en{' '}
          <a className="underline" href={N8N_BASE} target="_blank" rel="noreferrer">
            {N8N_BASE}
          </a>{' '}
          · JR2 MKT trackea enrollments y ejecuciones · editor visual en n8n
        </p>
        <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-mono bg-amber-50 border border-amber-200 text-amber-900">
          F11 prep · custom nodes en{' '}
          <span className="font-medium">packages/n8n-nodes-jr2os</span> · install en n8n
          pendiente
        </div>
      </header>

      <form onSubmit={(e) => void create(e)} className="bp-card p-3 mb-4 grid grid-cols-12 gap-2">
        <input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          placeholder="nombre del workflow"
          required
          className="col-span-4 border border-ink-900/15 rounded px-2 py-1.5 text-sm"
        />
        <input
          value={draftN8nId}
          onChange={(e) => setDraftN8nId(e.target.value)}
          placeholder="n8n workflow ID"
          required
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
        />
        <input
          value={draftTrigger}
          onChange={(e) => setDraftTrigger(e.target.value)}
          placeholder="evento trigger (p.ej. TAG_ADDED:tag-hot)"
          className="col-span-3 border border-ink-900/15 rounded px-2 py-1.5 text-sm font-mono"
        />
        <button type="submit" className="bp-btn-primary col-span-2 justify-center" disabled={busy}>
          <Plus className="size-3" /> Registrar
        </button>
      </form>
      {err && (
        <div className="bp-card p-2 mb-3 text-xs font-mono text-red-700 bg-red-50 border-red-200">
          {err}
        </div>
      )}

      <div className="bp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-alt text-xs font-mono uppercase tracking-wider text-ink-700/60">
            <tr>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Trigger</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Enroll.</th>
              <th className="px-3 py-2 text-right">Runs</th>
              <th className="px-3 py-2 text-right">v</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-ink-900/10">
                <td className="px-3 py-2">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] font-mono text-ink-700/60">
                    n8n: {r.n8nWorkflowId}
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-ink-700">
                  {r.triggerEvent ?? '—'}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={[
                      'bp-pill',
                      r.status === 'ACTIVE'
                        ? 'bg-emerald-50 text-emerald-700'
                        : r.status === 'PAUSED'
                          ? 'bg-amber-50 text-amber-700'
                          : r.status === 'ARCHIVED'
                            ? 'bg-ink-900/10 text-ink-700/60'
                            : 'bg-blueprint-50 text-blueprint-700',
                    ].join(' ')}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono">{r._count.enrollments}</td>
                <td className="px-3 py-2 text-right font-mono">{r._count.executions}</td>
                <td className="px-3 py-2 text-right font-mono">{r.version}</td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => void toggleStatus(r)}
                    className="bp-btn"
                    title={r.status === 'ACTIVE' ? 'Pausar' : 'Activar'}
                  >
                    {r.status === 'ACTIVE' ? (
                      <Pause className="size-3" />
                    ) : (
                      <Play className="size-3" />
                    )}
                  </button>
                  {r.editorUrl && (
                    <a
                      href={r.editorUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="bp-btn ml-1"
                      title="Abrir en n8n"
                    >
                      <ExternalLink className="size-3" />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => void remove(r)}
                    className="bp-btn text-red-700 ml-1"
                  >
                    <Trash2 className="size-3" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-xs text-ink-700/50">
                  Sin workflows registrados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 bp-card p-3 text-xs font-mono text-ink-700/70 space-y-1">
        <div className="font-medium text-ink-700">Cómo conectar JR2 ↔ n8n (F11):</div>
        <div>1. Construir e instalar <span className="font-bold">packages/n8n-nodes-jr2os</span> en el contenedor n8n.</div>
        <div>2. En n8n: crear workflow, usar nodos custom (OnClientTagAdded, SendSnippet, MoveStage…) + nodo HTTP final que POSTea a <span className="font-bold">/v1/marketing/workflows/n8n/webhook</span> firmado con signingSecret.</div>
        <div>3. Aquí: registrar el workflow con su n8n ID, definir triggerEvent y arrancarlo en estado ACTIVE.</div>
      </div>
    </div>
  )
}
